/**
 * useChatMessages Hook
 * Manages message loading, saving, and streaming for chat sessions
 */

import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { streamMessage } from "../services/aiChatService";
import { fetchUserContext } from "../services/chatContextService";
import { saveMessage } from "../components/chatdialog/utils/firestoreUtils";
import { getDb } from "../components/chatdialog/utils/firestoreUtils";
import { stripToolCallMarkers, sanitizeAiResponse, hasDisplayableContent } from "../components/chatdialog/utils/messageUtils";
import { extractToolCalls } from "../components/chatdialog/utils/toolParsing";
import { CHAT_TOOLS } from "../services/chatToolsService";
import { canSendMessage, incrementMessageCount } from "../api/firestore/services/chatMessageLimitService";

/**
 * Hook to manage chat messages
 * @param {string} userId - User ID
 * @param {string} chatId - Current chat session ID
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {Function} enhanceResponse - Function to enhance AI responses
 * @returns {Object} { messages, streamingText, isStreaming, sendMessage, loading, error }
 */
export const useChatMessages = (userId, chatId, isOpen, enhanceResponse) => {
        const { t, i18n } = useTranslation();
        const [messages, setMessages] = useState([]);
        const [streamingText, setStreamingText] = useState("");
        const [isStreaming, setIsStreaming] = useState(false);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState(null);
        const [userContext, setUserContext] = useState(null);

        const db = getDb();

        /**
         * Refresh user context
         * @param {Function} shouldSetState - Callback to determine if state should be updated
         * @returns {Promise<Object|null>} User context or null
         */
        const refreshUserContext = useCallback(
                async (shouldSetState = () => true) => {
                        if (!userId) {
                                if (shouldSetState()) {
                                        setUserContext(null);
                                }
                                return null;
                        }

                        try {
                                const context = await fetchUserContext(userId);

                                if (shouldSetState()) {
                                        setUserContext(context);
                                }

                                return context;
                        } catch (contextError) {
                                console.error("[useChatMessages][refreshUserContext] - Error fetching user context:", contextError);
                                return null;
                        }
                },
                [userId]
        );

        // Load user context when dialog opens
        useEffect(() => {
                if (!isOpen || !userId) {
                        return;
                }

                let isMounted = true;

                const loadContext = async () => {
                        try {
                                await refreshUserContext(() => isMounted);
                        } catch (contextError) {
                                console.error("[useChatMessages][loadContext] - Error loading user context:", contextError);

                                if (isMounted) {
                                        setError(t("ai-chat-error-loading"));
                                }
                        }
                };

                loadContext();

                return () => {
                        isMounted = false;
                };
        }, [userId, isOpen, refreshUserContext, t]);

        // Subscribe to messages for selected chat
        useEffect(() => {
                if (!isOpen || !userId || !chatId) {
                        setMessages([]);
                        setStreamingText("");
                        return;
                }

                setLoading(true);

                const unsubscribe = db
                        .collection("Users")
                        .doc(userId)
                        .collection("AIChat")
                        .doc(chatId)
                        .collection("Messages")
                        .orderBy("timestamp", "asc")
                        .onSnapshot((snapshot) => {
                                const newMessages = snapshot.docs.map((doc) => ({
                                        id: doc.id,
                                        ...doc.data(),
                                }));

                                setMessages(newMessages);
                                setLoading(false);
                        }, (snapshotError) => {
                                console.error("[useChatMessages][messagesSnapshot] - Error loading chat messages:", snapshotError);
                                setLoading(false);
                        });

                return () => unsubscribe();
        }, [userId, isOpen, chatId, db]);

        /**
         * Send a message to the AI
         * @param {string} messageText - Message text to send
         * @param {Function} runTool - Function to execute tools
         * @param {Function} interpretToolResults - Function to interpret tool results
         * @param {string} [chatIdOverride=null] - Optional chat ID to use instead of state
         * @returns {Promise<void>}
         */
        const sendMessage = useCallback(async (messageText, runTool, interpretToolResults, chatIdOverride = null) => {
                const targetChatId = chatIdOverride || chatId;

                if (!messageText?.trim() || !targetChatId || !userId) {
                        return;
                }

                const userMessage = messageText.trim();
                setError(null);
                setLoading(true);
                setIsStreaming(true);
                setStreamingText("");

                // Track if tools were called for interpretation
                let toolsExecuted = false;

                try {
                        // Check message limit before sending
                        const limitCheck = await canSendMessage(userId);
                        if (!limitCheck.canSend) {
                                const errorMsg = limitCheck.reason === "limit_exceeded"
                                        ? t("chat-limit-exceeded", { limit: limitCheck.limit || 20 })
                                        : t("ai-chat-error");
                                setError(errorMsg);
                                setLoading(false);
                                setIsStreaming(false);
                                setStreamingText("");
                                return;
                        }

                        // Save user message
                        await saveMessage(userId, targetChatId, userMessage, "user");

                        // Increment message count after successful save
                        incrementMessageCount(userId).catch((countError) => {
                                console.warn("[useChatMessages][sendMessage] - Error incrementing message count:", countError);
                                // Don't block message if count update fails
                        });

                        // Refresh context before sending to AI
                        let context = userContext;
                        try {
                                const refreshedContext = await refreshUserContext();
                                if (refreshedContext) {
                                        context = refreshedContext;
                                }
                        } catch (contextError) {
                                console.error(
                                        "[useChatMessages][sendMessage] - Error refreshing user context:",
                                        contextError
                                );
                        }

                        // Get current chat history for this chat
                        const messagesSnapshot = await db
                                .collection("Users")
                                .doc(userId)
                                .collection("AIChat")
                                .doc(targetChatId)
                                .collection("Messages")
                                .orderBy("timestamp", "asc")
                                .get();

                        const currentHistory = messagesSnapshot.docs.map((doc) => ({
                                id: doc.id,
                                ...doc.data(),
                        }));

                        // Stream AI response
                        // Note: isStreaming remains true throughout this call, including tool execution and AI thinking phases
                        let fullResponse = "";
                        await streamMessage(
                                userId,
                                userMessage,
                                context,
                                currentHistory,
                                (chunk) => {
                                        fullResponse += chunk;
                                        setStreamingText(stripToolCallMarkers(fullResponse));
                                },
                                { language: i18n.language }
                        );

                        const toolCalls = extractToolCalls(fullResponse);
                        const uniqueToolCallsMap = new Map();

                        toolCalls.forEach((call) => {
                                const payloadKey =
                                        call.payload && typeof call.payload === "object"
                                                ? JSON.stringify(call.payload)
                                                : String(call.payload ?? "");
                                const key = `${call.id}|${payloadKey}`;

                                if (!uniqueToolCallsMap.has(key)) {
                                        uniqueToolCallsMap.set(key, call);
                                }
                        });

                        const uniqueToolCalls = Array.from(uniqueToolCallsMap.values());
                        let sanitizedResponse = sanitizeAiResponse(fullResponse);

                        if (!hasDisplayableContent(sanitizedResponse) && uniqueToolCalls.length > 0) {
                                const primaryTool = CHAT_TOOLS[uniqueToolCalls[0].id];
                                sanitizedResponse = t("ai-chat-tool-auto-fetching", {
                                        tool: primaryTool ? t(primaryTool.labelKey) : uniqueToolCalls[0].id,
                                });
                        }

                        // Enhance response with animal IDs if it mentions listing suspicious animals
                        if (hasDisplayableContent(sanitizedResponse) && enhanceResponse) {
                                try {
                                        sanitizedResponse = await enhanceResponse(sanitizedResponse);
                                } catch (enhanceError) {
                                        console.error("[useChatMessages][sendMessage] - Error enhancing response:", enhanceError);
                                        // Continue with original response if enhancement fails
                                }
                        }

                        if (hasDisplayableContent(sanitizedResponse)) {
                                await saveMessage(userId, targetChatId, sanitizedResponse, "ai", {
                                        type: "ai-response",
                                        toolCalls: uniqueToolCalls,
                                });
                        }

                        if (uniqueToolCalls.length > 0 && runTool) {
                                for (const call of uniqueToolCalls) {
                                        await runTool(call.id, {
                                                skipUserMessage: true,
                                                suppressLoader: true,
                                                source: "auto",
                                                payload: call.payload,
                                        });
                                }
                                toolsExecuted = true;
                        }
                } catch (error) {
                        console.error("[useChatMessages][sendMessage] - Error sending message:", error);
                        // Stop streaming indicator on error
                        setIsStreaming(false);
                        setError(error.message || t("ai-chat-error"));

                        // Save error message
                        const errorMessage = t("ai-chat-error") || "Sorry, I encountered an error. Please try again.";
                        await saveMessage(userId, targetChatId, errorMessage, "ai");
                } finally {
                        setLoading(false);
                        setIsStreaming(false);
                        setStreamingText("");
                }

                // Automatically interpret tool results after execution (outside finally block)
                if (toolsExecuted && interpretToolResults) {
                        try {
                                await interpretToolResults(0);
                        } catch (interpretError) {
                                console.error("[useChatMessages][sendMessage] - Error interpreting tool results:", interpretError);
                        }
                }
        }, [userId, chatId, userContext, refreshUserContext, t, i18n.language, enhanceResponse, db]);

        return {
                messages,
                streamingText,
                isStreaming,
                sendMessage,
                loading,
                error,
        };
};

